// 2026-05-26 - FormuLearning - Step 1 분자 정보 섹션
// 파일 위치: frontend/src/features/step1/MoleculeSection.tsx

import { useEffect, useState } from 'react'
import Collecting from './Collecting'

interface Props {
  commonName: string | null
  iupacName: string | null
  smiles: string | null
  casNumber: string | null
  inchi: string | null
  molecularImage: string | null
  pubchemExtra: Record<string, unknown> | null
  source?: string
  loading?: boolean
}

export default function MoleculeSection({ commonName, iupacName, smiles, casNumber, inchi, molecularImage, pubchemExtra, source, loading }: Props) {
  const [zoomed, setZoomed] = useState(false)

  useEffect(() => {
    if (!zoomed) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomed(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [zoomed])

  if (!commonName) {
    return (
      <div className="mb-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">Input Molecule</h4>
        {loading ? <Collecting /> : <p className="text-[11px] text-text-muted">표시할 분자 정보가 없습니다.</p>}
      </div>
    )
  }

  const fields = [
    { label: 'Common Name', value: commonName },
    { label: 'IUPAC Name', value: iupacName },
    { label: 'SMILES', value: smiles },
    { label: 'CAS Number', value: casNumber },
    { label: 'InChI', value: inchi },
  ]

  const extraFields = pubchemExtra ? [
    { label: 'MW', value: pubchemExtra.molecular_weight, unit: 'g/mol' },
    { label: 'Formula', value: pubchemExtra.molecular_formula, unit: '' },
    { label: 'XLogP', value: pubchemExtra.xlogp, unit: '' },
    { label: 'TPSA', value: pubchemExtra.tpsa, unit: 'Å²' },
    { label: 'HBD', value: pubchemExtra.hbond_donor, unit: '' },
    { label: 'HBA', value: pubchemExtra.hbond_acceptor, unit: '' },
  ].filter((f) => f.value != null) : []

  return (
    <div className="panel-in mb-4">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Input Molecule</h4>
        {source && (
          <span
            className="rounded-full px-2 py-0.5 text-[9px] font-medium"
            style={{ background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
          >{source}</span>
        )}
      </div>
      <div className="flex gap-4">
        <div className="flex-1 grid grid-cols-1 gap-2">
          {fields.map((f) => (
            <div key={f.label} className="flex gap-2">
              <span className="w-28 shrink-0 text-[11px] font-medium text-text-secondary">{f.label}</span>
              <span className="break-all text-[11px] text-text-body">{f.value || '—'}</span>
            </div>
          ))}
        </div>
        {molecularImage && (
          <div className="shrink-0">
            <img
              src={molecularImage}
              alt={commonName || 'Molecule'}
              className="cursor-zoom-in rounded border transition-shadow hover:shadow-md"
              style={{ width: 140, height: 140, borderColor: 'var(--border-soft)', background: '#fff' }}
              title="클릭하면 크게 보기"
              onClick={() => setZoomed(true)}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          </div>
        )}
      </div>
      {extraFields.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {extraFields.map((f) => (
            <div
              key={f.label}
              className="rounded border px-2 py-1 text-center"
              style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
            >
              <p className="text-[9px] text-text-secondary">{f.label}</p>
              <p className="text-[11px] font-semibold text-accent-purple">{String(f.value)}{f.unit ? ` ${f.unit}` : ''}</p>
            </div>
          ))}
        </div>
      )}
      {zoomed && molecularImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setZoomed(false)}
        >
          <div
            className="relative rounded-lg p-4"
            style={{ background: '#fff', boxShadow: 'var(--elev-2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomed(false)}
              className="absolute -right-3 -top-3 flex h-7 w-7 items-center justify-center rounded-full bg-bg-card text-text-secondary hover:text-text-body"
              style={{ boxShadow: 'var(--elev-2)' }}
              aria-label="닫기"
            >✕</button>
            <img
              src={molecularImage}
              alt={commonName || 'Molecule'}
              className="cursor-zoom-out rounded"
              style={{ width: 420, height: 420, background: '#fff' }}
            />
            <p className="mt-1 text-center text-[11px] text-text-secondary">{commonName}</p>
          </div>
        </div>
      )}
    </div>
  )
}
